import {useTranslation} from '@aboutyou/i18n';
import {lazy} from '@aboutyou/lazy-imports';
import {
  COLORS,
  FONT_SIZES,
  FONT_WEIGHTS,
  HandleClickOutside,
} from '@aboutyou/ui-components';
import {styled} from '@linaria/react';
import React, {FC, MouseEventHandler, useRef, useState} from 'react';
import {LanguageCountrySwitchFlyout} from './LanguageCountrySwitchFlyout';
import {CountryFlag} from './shared';

const LanguageCountrySwitchPopup = lazy(() =>
  import('./LanguageCountrySwitchPopup').then(mod => ({
    default: mod.LanguageCountrySwitchPopup,
  })),
);

type CurrentSelectionProps = {
  selectedCountryFlagSrc?: string;
  selectedCountryLabel?: string;
  selectedLanguageLabel?: string;
};

export type Languages = Array<{
  isSelected: boolean;
  label: string;
  onClick?: MouseEventHandler<HTMLAnchorElement>;
  linkTarget?: string;
}>;

export type Countries = Array<{
  flagSrc?: string;
  isSelected: boolean;
  label: string;
  linkTarget?: string;
}>;

export type SelectOptionsProps = {
  countries: Countries;
  languages?: Languages;
};

export type LanguageCountrySwitchProps = CurrentSelectionProps &
  SelectOptionsProps & {
    className?: string;
    labelAndBorderColor?: COLORS;
    openAs: 'flyout' | 'popup';
    onHover?: (status: boolean) => void;
    overlayWrapperZIndex?: number;
    shouldUseBorderOnHover?: boolean;
    shouldDisplayArrowOnHover?: boolean;
  };

export const LanguageCountrySwitch: FC<LanguageCountrySwitchProps> = ({
  className,
  countries,
  labelAndBorderColor,
  languages,
  openAs,
  overlayWrapperZIndex,
  selectedCountryFlagSrc,
  selectedCountryLabel,
  selectedLanguageLabel,
  shouldUseBorderOnHover = true,
  shouldDisplayArrowOnHover = false,
  onHover,
}) => {
  const ref = useRef(null);

  // Open state will be triggered by hover, but click "sets" open state even if not hovered.
  // This is especially relevant for touch devices.
  const [
    isLanguageSwitchComponentHovered,
    setIsLanguageSwitchComponentHovered,
  ] = useState(false);
  const [forceOpenPopup, setForceOpenPopup] = useState(false);
  const [flyoutOpen, setFlyoutOpen] = useState(false);

  const longSelectedCountryLabel = countries.find(c => c.isSelected)?.label;

  const shouldShowPopup =
    (isLanguageSwitchComponentHovered || forceOpenPopup) && openAs === 'popup';

  const handleVisibilityState = (open: boolean) => {
    setIsLanguageSwitchComponentHovered(open);
    onHover?.(open);
  };

  const {t} = useTranslation();

  return (
    <Wrapper
      onMouseEnter={() => handleVisibilityState(true)}
      onMouseLeave={() => handleVisibilityState(false)}
      data-should-display-arrow-on-hover={shouldDisplayArrowOnHover}
      $forceOpen={forceOpenPopup}
      $popupEnabled={openAs === 'popup'}
      $shouldUseBorder={shouldUseBorderOnHover}
      $overlayWrapperZIndex={overlayWrapperZIndex}
      ref={ref}
      role="region"
      aria-label={t('Language and country switch')}
    >
      <Switch
        $shouldUseBorder={shouldUseBorderOnHover}
        className={className}
        data-testid="languageCountrySwitch"
        onClick={() =>
          openAs === 'flyout'
            ? setFlyoutOpen(prev => !prev)
            : setForceOpenPopup(prev => !prev)
        }
      >
        <>
          <SelectedCountry>
            {selectedCountryFlagSrc && (
              <SelectedCountryFlag
                data-testid={`languageCountrySwitchSelectedCountryFlag`}
                height="11"
                src={selectedCountryFlagSrc}
                width="13"
                alt={`${longSelectedCountryLabel} ${t('flag')}`}
              />
            )}
            {selectedCountryLabel && (
              <SelectedCountryLabel
                data-testid={`languageCountrySwitchSelectedCountry_${selectedCountryLabel}`}
                $labelAndBorderColor={labelAndBorderColor}
              >
                {selectedCountryLabel}
              </SelectedCountryLabel>
            )}
          </SelectedCountry>
          {selectedLanguageLabel && (
            <SelectedLanguageLabel
              data-testid={`languageCountrySwitchSelectedLanguage_${selectedLanguageLabel}`}
              $labelAndBorderColor={labelAndBorderColor}
            >
              {selectedLanguageLabel}
            </SelectedLanguageLabel>
          )}
        </>
      </Switch>
      {flyoutOpen && (
        <HandleClickOutside
          wrapperRef={ref}
          onClose={() => {
            // We do not need this click if we are in flyout mode as the flyout will be closed by the flyout itself
            if (openAs === 'popup') {
              setForceOpenPopup(false);

              handleVisibilityState(false);
            }
          }}
        />
      )}
      {openAs === 'flyout' && (
        <LanguageCountrySwitchFlyout
          countries={countries}
          languages={languages}
          onClose={() => setFlyoutOpen(false)}
          open={flyoutOpen}
          selectedCountryFlagSrc={selectedCountryFlagSrc!}
          selectedCountryLabel={longSelectedCountryLabel!}
        />
      )}
      {shouldShowPopup && (
        <LanguageCountrySwitchPopupStyled
          $overlayWrapperZIndex={overlayWrapperZIndex}
          countries={countries}
          languages={languages}
          selectedCountryFlagSrc={selectedCountryFlagSrc!}
          selectedCountryLabel={longSelectedCountryLabel!}
        />
      )}
    </Wrapper>
  );
};

const Switch = styled.button<{
  $shouldUseBorder: boolean;
}>`
  align-items: center;
  background-color: ${COLORS.Grey4};
  box-sizing: border-box;
  color: ${COLORS.Black};
  cursor: pointer;
  display: flex;
  flex: 0 0 auto;
  outline: none;
  padding: 0 0.5rem;

  border: ${props =>
    props.$shouldUseBorder ? '1px solid transparent' : 'none'};

  // eslint-disable-next-line local-rules/css-layout-animation
  transition: ${props =>
    props.$shouldUseBorder ? `border 0.3s ease-in-out` : 'unset'};
`;

const Wrapper = styled.div<{
  $forceOpen: boolean;
  $popupEnabled: boolean;
  $shouldUseBorder: boolean;
  $shouldDisplayArrowOnHover?: boolean;
  $overlayWrapperZIndex?: number;
}>`
  cursor: pointer;
  position: relative;
  -webkit-tap-highlight-color: transparent;

  @media (hover: hover) {
    &:hover {
      ${Switch} {
        border: ${props =>
          props.$shouldUseBorder ? `1px solid ${COLORS.Black}` : 'none'};
      }

      &[data-should-display-arrow-on-hover='true'] {
        // Pseudo-Element to hide the lower shadow of the arrow
        &::before {
          content: '';
          background-color: ${COLORS.White};
          width: 40px;
          height: 12px;
          position: absolute;
          bottom: -16px;
          left: 50%;
          transform: translateX(-50%);
          overflow: hidden;
          // +1 to ensure we are above the arrow to hide its bottom shadow
          z-index: ${({$overlayWrapperZIndex}) =>
            $overlayWrapperZIndex ? `${$overlayWrapperZIndex + 1}` : 'auto'};
        }

        // Pseudo-Element to display the arrow
        &::after {
          content: '';

          background: ${COLORS.White};
          border: 4px solid transparent;

          position: absolute;
          bottom: -4px;
          left: 45%;
          size: 8px;
          box-shadow: 0 0 12px 0 rgba(0, 0, 0, 0.2);

          transform: translateY(50%) rotate(45deg);

          z-index: ${({$overlayWrapperZIndex}) =>
            $overlayWrapperZIndex ? `${$overlayWrapperZIndex}` : 'auto'};
        }
      }
    }
  }

  ${Switch} {
    border: ${({$forceOpen, $popupEnabled, $shouldUseBorder}) =>
      $forceOpen && $popupEnabled && $shouldUseBorder
        ? `1px solid ${COLORS.Black}`
        : 'none'};
  }
`;

const SelectedCountry = styled.div`
  align-items: center;
  display: flex;
`;

const SelectedCountryFlag = styled(CountryFlag)`
  margin-right: 0.25rem;
`;

const SelectedCountryLabel = styled.span<{
  $labelAndBorderColor?: COLORS;
}>`
  font-size: ${FONT_SIZES.px10};
  font-weight: ${FONT_WEIGHTS.bold};
  line-height: 1;
  color: ${props =>
    props.$labelAndBorderColor ? `${props.$labelAndBorderColor}` : 'inherit'};
`;

export const SelectedLanguageLabel = styled.span<{
  $labelAndBorderColor?: COLORS;
}>`
  border: 1px solid
    ${props =>
      props.$labelAndBorderColor
        ? `${props.$labelAndBorderColor}`
        : `${COLORS.Black}`};
  border-radius: 1px;
  box-sizing: border-box;
  color: ${props =>
    props.$labelAndBorderColor
      ? `${props.$labelAndBorderColor}`
      : `${COLORS.Black}`};
  display: block;
  font-size: ${FONT_SIZES.px8};
  line-height: 1;
  margin-left: 0.25rem;
  padding: 0.125rem;
`;

const LanguageCountrySwitchPopupStyled = styled(LanguageCountrySwitchPopup)<{
  $overlayWrapperZIndex?: number;
}>`
  /* z-index: ${props =>
    props.$overlayWrapperZIndex
      ? `${props.$overlayWrapperZIndex} !important`
      : 'auto'}; */
`;
